import { useEffect, useState } from "react";
import api, { apiError, API, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import * as Icons from "lucide-react";

// Bon d'intervention SIGNA CARE (PDF imprimable). scope = "equipment" | "site".
export function WorkOrderDialog({ scope, equipmentId, siteId, defaultDue = "", trigger, onCreated }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ title: "", description: "", priority: "normale", due_date: defaultDue, assigned_to: "" });

  useEffect(() => {
    if (!open) return;
    setF({ title: "", description: "", priority: "normale", due_date: defaultDue || "", assigned_to: "" });
    api.get("/users/assignable").then((r) => setUsers(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const create = async () => {
    setSaving(true);
    try {
      const payload = { scope, equipment_id: equipmentId, site_id: siteId, title: f.title,
        description: f.description, priority: f.priority, due_date: f.due_date || null,
        assigned_to: f.assigned_to || null };
      const { data } = await api.post("/care/work-orders", payload);
      toast.success(`Bon d'intervention ${data.reference} créé`);
      window.open(`${API}/care/work-orders/${data.id}/pdf?auth=${getToken()}`, "_blank");
      setOpen(false);
      onCreated?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || <Button size="sm" variant="outline" data-testid="wo-open-btn"><Icons.ClipboardList className="mr-1.5 h-4 w-4" />Bon d'intervention</Button>}
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="wo-dialog">
        <DialogHeader>
          <DialogTitle>Bon d'intervention</DialogTitle>
          <DialogDescription>Document imprimable à transmettre au technicien.{scope === "site" ? " Portée : tout le site." : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Intitulé de l'intervention</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Ex. Remplacement plots solaires" data-testid="wo-title" /></div>
          <div className="space-y-1.5"><Label>Description / consignes</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Détails de l'intervention à réaliser…" data-testid="wo-description" /></div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Échéance</Label><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="wo-due" /></div>
            <div className="space-y-1.5"><Label>Priorité</Label>
              <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
                <SelectTrigger data-testid="wo-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="basse">Basse</SelectItem>
                  <SelectItem value="normale">Normale</SelectItem>
                  <SelectItem value="haute">Haute</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>À réaliser par</Label>
            <Select value={f.assigned_to} onValueChange={(v) => setF({ ...f, assigned_to: v })}>
              <SelectTrigger data-testid="wo-assignee"><SelectValue placeholder="Choisir un intervenant" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={create} disabled={saving || !f.title.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wo-create">
            <Icons.FileText className="mr-1.5 h-4 w-4" />Créer & télécharger le PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
